/**
 * The sequencer's scheduling arithmetic. Notes are never played "now": a timer wakes every
 * 25 ms or so, and every note that starts inside the next `LOOKAHEAD` seconds is handed to
 * WebAudio with its exact time on the audio clock. Timers jitter; the audio clock does not.
 * No WebAudio in here, so it can be tested with a pretend clock. Pure.
 */

import { type Compiled, type NoteEvent, STEPS_PER_BAR } from './types';

/** Seconds of audio scheduled ahead of the clock. */
export const LOOKAHEAD = 0.15;
/** How often the timer fires, ms: several wake-ups per look-ahead, so one late timer is harmless. */
export const TICK_MS = 25;
/** A note this far in the past was missed (a throttled background tab): skipped, never played late. */
export const LATE = 0.04;

/** Seconds per step (a 16th note) at `bpm` quarter notes a minute. */
export function stepSeconds(bpm: number): number {
  return 60 / bpm / (STEPS_PER_BAR / 4);
}

/** Where a playing song has got to: the next event, and how many times it has looped. */
export interface Cursor {
  index: number;
  pass: number;
}

export interface Due {
  readonly event: NoteEvent;
  /** Audio-clock seconds. */
  readonly time: number;
}

/**
 * The events that start before `horizon` and have not been handed out yet, in order; moves the
 * cursor past them. `start` is the audio time of step 0. Events more than `LATE` behind `now`
 * are passed over, and the grid never moves, so the music stays in time with itself afterwards.
 */
export function collectDue(
  song: Compiled, cursor: Cursor, start: number, stepSec: number, now: number, horizon: number,
): Due[] {
  const out: Due[] = [];
  if (song.events.length === 0 || song.steps <= 0) return out;
  if (song.loop) {
    // Back from a long sleep: jump straight to the current pass instead of walking every loop missed.
    const pass = Math.floor((now - LATE - start) / (song.steps * stepSec));
    if (pass > cursor.pass) {
      cursor.pass = pass;
      cursor.index = 0;
    }
  }
  for (;;) {
    if (cursor.index >= song.events.length) {
      if (!song.loop) break;
      cursor.index = 0;
      cursor.pass++;
    }
    const event = song.events[cursor.index];
    if (!event) break;
    const time = start + (event.step + cursor.pass * song.steps) * stepSec;
    if (time >= horizon) break;
    cursor.index++;
    if (time >= now - LATE) out.push({ event, time });
  }
  return out;
}

/** When a song that plays once is over: its length, plus a moment for the last release. */
export function onceEnds(song: Compiled, start: number, stepSec: number): number {
  return start + song.steps * stepSec + 0.3;
}
